// Helperfunctions for map page

import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import '../../style/map.scss';
import { fetchMETAR, fetchTAFV2, getNOTAMListFromAllData } from '../helperfunctions';

// Removes all markers from map, should be called first when the state of the filter on the map is changed
export function clearMarkers(markers) {
  markers.forEach((marker) => marker.remove());
}

// lat and lng coordinates are formatted as seconds in the data object, need to divide by 3600 to get the real value
export function formatLongLat(number) {
  return number / 3600;
}

// used to place the map "camera" in the center between the departure and arrival airports
export function getLngLatMidpoints(data) {
  const depAirport = data?.WP_List?.[0]?.WayPoint?.[0];
  const arrAirport =
    data?.WP_List?.[0]?.WayPoint?.[
    (data.WP_List?.[0]?.WayPoint || []).length - 1
    ];

  const lngMidpoint =
    (formatLongLat(depAirport?.nLon || 0) +
      formatLongLat(arrAirport?.nLon || 0)) /
    2;
  const latMidpoint =
    (formatLongLat(depAirport?.nLat || 0) +
      formatLongLat(arrAirport?.nLat || 0)) /
    2;

  const midpoints = { lng: lngMidpoint, lat: latMidpoint };
  return midpoints;
}

// gets all waypoints coordinates for the main route and pushes them into a list
export function getMainRouteCoordinates(data) {
  let mainRouteCoordinates = [];
  for (const waypoint of data?.WP_List[0]?.WayPoint || []) {
    const formattedLat = formatLongLat(waypoint?.nLat || 0);
    const formattedLong = formatLongLat(waypoint?.nLon || 0);
    mainRouteCoordinates.push([formattedLong, formattedLat]);
  }

  return mainRouteCoordinates;
}

// add a main route line with waypoints
export function addMainRouteWayPoints(
  map,
  data,
  markerList,
  handleOpenAirportModal,
  mapLayers,
  mapSources
) {
  if (!data) return;
  const mainRouteCoordinates = getMainRouteCoordinates(data);
  const notamList = getNOTAMListFromAllData(data);

  // departure airport
  const depWaypointId = data?.WP_List?.[0]?.WayPoint?.[0]?.sWaypointId;
  const depAirportName = data?.WP_List?.[0]?.WayPoint?.[0]?.sWaypointLongName;
  const depAirportICAO = data?.sDepICAO;
  let depAirportTAF;
  let depAirportMETAR;

  // fetching taf data (using V2 endpoint with JSON format)
  fetchTAFV2(depAirportICAO)
    .then((res) => res.json())
    .then(
      (res) =>
      (depAirportTAF = res?.[0]?.rawTAF
        ? res[0].rawTAF
        : 'TAF data not found')
    );

  // fetching metar data
  fetchMETAR(depAirportICAO)
    .then((res) => res.json())
    .then((res) => (depAirportMETAR = res?.[0]?.METAR?.[0]));

  // Create a HTML element for the marker with the airport code
  const depWaypointContainer = document.createElement('div');
  depWaypointContainer.className = 'waypoint-container';

  const depMarker = document.createElement('div');
  depMarker.className = 'dep-arr-waypoint-img';

  const depIdElement = document.createElement('span');
  depIdElement.className = 'dep-arr-waypoint-text';
  const depIdText = document.createTextNode(depWaypointId);
  depIdElement.appendChild(depIdText);

  depWaypointContainer.appendChild(depMarker);
  depWaypointContainer.appendChild(depIdElement);

  const depWaypoint = new mapboxgl.Marker(depWaypointContainer)
    .setLngLat(mainRouteCoordinates[0])
    .addTo(map.current);
  depMarker.onclick = () =>
    handleOpenAirportModal(
      depAirportName,
      data.sDepIATA,
      depAirportMETAR,
      depAirportTAF,
      depAirportICAO,
      notamList
    );

  markerList.push(depWaypoint);

  // waypoints between dep and arr airport
  for (let i = 1; i < (data?.WP_List?.[0]?.WayPoint || []).length - 1; i++) {
    const waypointId = data.WP_List[0].WayPoint[i]?.sWaypointId;

    const pointElement = document.createElement('div');
    pointElement.className = 'waypoint-container z-indexZero';

    const pointImg = document.createElement('div');
    pointImg.className = 'waypoint-img';

    const pointId = document.createElement('span');
    pointId.className = 'route-waypoint-text';
    const pointIdText = document.createTextNode(waypointId || '');
    pointId.appendChild(pointIdText);

    pointElement.appendChild(pointImg);
    pointElement.appendChild(pointId);

    const marker = new mapboxgl.Marker(pointElement)
      .setLngLat(mainRouteCoordinates[i])
      .addTo(map.current);
    markerList.push(marker);
  }

  // arr airport
  const arrWaypointIndex = (data?.WP_List?.[0]?.WayPoint || []).length - 1;
  const arrWaypointId =
    data?.WP_List?.[0]?.WayPoint?.[arrWaypointIndex || 0]?.sWaypointId;
  const arrAirportName =
    data?.WP_List?.[0]?.WayPoint?.[arrWaypointIndex || 0]?.sWaypointLongName;
  const arrAirportICAO = data?.sDestICAO;
  let arrAirportTAF;
  let arrAirportMETAR;

  // fetching taf data (using V2 endpoint with JSON format)
  fetchTAFV2(arrAirportICAO)
    .then((res) => res.json())
    .then(
      (res) =>
      (arrAirportTAF = res?.[0]?.rawTAF
        ? res[0].rawTAF
        : 'TAF data not found')
    );

  // fetching metar data
  fetchMETAR(arrAirportICAO)
    .then((res) => res.json())
    .then((res) => (arrAirportMETAR = res?.[0]?.METAR?.[0]));

  // Create a HTML element for the marker with the airport code
  const arrWaypointContainer = document.createElement('div');
  arrWaypointContainer.className = 'waypoint-container';

  const arrMarker = document.createElement('div');
  arrMarker.className = 'dep-arr-waypoint-img';

  const arrIdElement = document.createElement('span');
  arrIdElement.className = 'dep-arr-waypoint-text';
  const arrIdText = document.createTextNode(arrWaypointId || '');
  arrIdElement.appendChild(arrIdText);

  arrWaypointContainer.appendChild(arrMarker);
  arrWaypointContainer.appendChild(arrIdElement);

  const arrWaypointMarker = new mapboxgl.Marker(arrWaypointContainer)
    .setLngLat(mainRouteCoordinates[mainRouteCoordinates.length - 1])
    .addTo(map.current);
  arrMarker.onclick = () =>
    handleOpenAirportModal(
      arrAirportName,
      data.sDestIATA,
      arrAirportMETAR,
      arrAirportTAF,
      arrAirportICAO,
      notamList
    );

  markerList.push(arrWaypointMarker);

  // draw main route line using mapbox
  const addWaypointLine = () => {
    if (!map.current.getSource('waypointLine')) {
      map.current.addSource('waypointLine', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: mainRouteCoordinates,
          },
        },
      });
      if (mapSources) mapSources.add('waypointLine');
    }

    if (!map.current.getLayer('waypointLine')) {
      map.current.addLayer({
        id: 'waypointLine',
        type: 'line',
        source: 'waypointLine',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#000000',
          'line-width': 3,
        },
      });
      if (mapLayers) mapLayers.add('waypointLine');
    }
  };

  if (map.current.isStyleLoaded()) {
    addWaypointLine();
  } else {
    map.current.once('style.load', addWaypointLine);
  }
}

// paints out the mainRoute line with waypoints on the map as well as places the dep and arr airport markers which are color coded based on flight category from a metar fetch
export function addColorCodedMainRouteWaypoints(
  map,
  data,
  markerList,
  handleOpenAirportModal,
  mapLayers,
  mapSources
) {
  const mainRouteCoordinates = getMainRouteCoordinates(data);
  const notamList = getNOTAMListFromAllData(data);

  // dep airport
  const depWaypointId = data?.WP_List?.[0]?.WayPoint?.[0]?.sWaypointId;
  const depAirportName = data?.WP_List?.[0]?.WayPoint?.[0]?.sWaypointLongName;
  const depAirportICAO = data?.sDepICAO;
  let depAirportTAF;
  let depAirportMETAR;
  let depAirportFlightCategory;

  const depWaypointContainer = document.createElement('div');
  depWaypointContainer.className = 'waypoint-container';

  const depMarker = document.createElement('div');
  //depMarker.className = 'dep-arr-waypoint-img'

  const depIdElement = document.createElement('span');
  depIdElement.className = 'dep-arr-waypoint-text';

  // fetching taf data (using V2 endpoint with JSON format)
  fetchTAFV2(depAirportICAO)
    .then((res) => res.json())
    .then(
      (res) =>
      (depAirportTAF = res?.[0]?.rawTAF
        ? res[0].rawTAF
        : 'TAF data not found')
    );

  // fetching metar data
  fetchMETAR(depAirportICAO)
    .then((res) => res.json())
    .then((res) => {
      depAirportMETAR = res?.[0]?.METAR?.[0];
      depAirportFlightCategory = res?.[0]?.METAR?.[0]?.flight_category?.[0];

      // color of marker based on the color category (data is in from metar fetch)
      if (depAirportFlightCategory === 'LIFR') {
        depMarker.className = 'dep-arr-waypoint-img-magenta';
      } else if (depAirportFlightCategory === 'IFR') {
        depMarker.className = 'dep-arr-waypoint-img-red';
      } else if (depAirportFlightCategory === 'MVFR') {
        depMarker.className = 'dep-arr-waypoint-img-blue';
      } else if (depAirportFlightCategory === 'VFR') {
        depMarker.className = 'dep-arr-waypoint-img-green';
      } else {
        depMarker.className = 'dep-arr-waypoint-img-brown';
      }

      const depWaypoint = new mapboxgl.Marker(depWaypointContainer)
        .setLngLat(mainRouteCoordinates[0])
        .addTo(map.current);
      depMarker.onclick = () =>
        handleOpenAirportModal(
          depAirportName,
          data.sDepIATA,
          depAirportMETAR,
          depAirportTAF,
          depAirportICAO,
          notamList
        );

      markerList.push(depWaypoint);
    });

  const depIdText = document.createTextNode(depWaypointId);
  depIdElement.appendChild(depIdText);

  depWaypointContainer.appendChild(depMarker);
  depWaypointContainer.appendChild(depIdElement);

  // waypoints between dep and arr airport
  for (let i = 1; i < (data?.WP_List?.[0]?.WayPoint || []).length - 1; i++) {
    const waypointId = data.WP_List[0].WayPoint[i]?.sWaypointId;

    const pointElement = document.createElement('div');
    pointElement.className = 'waypoint-container z-indexZero';

    const pointImg = document.createElement('div');
    pointImg.className = 'waypoint-img';

    const pointId = document.createElement('span');
    pointId.className = 'route-waypoint-text';
    const pointIdText = document.createTextNode(waypointId || '');
    pointId.appendChild(pointIdText);

    pointElement.appendChild(pointImg);
    pointElement.appendChild(pointId);

    const marker = new mapboxgl.Marker(pointElement)
      .setLngLat(mainRouteCoordinates[i])
      .addTo(map.current);
    markerList.push(marker);
  }

  // arr airport
  const arrWaypointIndex = (data?.WP_List?.[0]?.WayPoint || []).length - 1;
  const arrWaypointId =
    data?.WP_List?.[0]?.WayPoint[arrWaypointIndex || 0]?.sWaypointId;
  const arrAirportName =
    data?.WP_List?.[0]?.WayPoint[arrWaypointIndex || 0]?.sWaypointLongName;
  const arrAirportICAO = data.sDestICAO;
  let arrAirportTAF;
  let arrAirportMETAR;
  let arrAirportFlightCategory;

  const arrWaypointContainer = document.createElement('div');
  arrWaypointContainer.className = 'waypoint-container';

  const arrMarker = document.createElement('div');
  //arrMarker.className = 'dep-arr-waypoint-img'

  const arrIdElement = document.createElement('span');
  arrIdElement.className = 'dep-arr-waypoint-text';

  // fetching taf data (using V2 endpoint with JSON format)
  fetchTAFV2(arrAirportICAO)
    .then((res) => res.json())
    .then(
      (res) =>
      (arrAirportTAF = res?.[0]?.rawTAF
        ? res[0].rawTAF
        : 'TAF data not found')
    );

  // fetching metar data for arr airport
  fetchMETAR(arrAirportICAO)
    .then((res) => res.json())
    .then((res) => {
      arrAirportMETAR = res?.[0]?.METAR?.[0];
      arrAirportFlightCategory = res?.[0]?.METAR?.[0]?.flight_category?.[0];

      // color of marker based on the color category (data is in from metar fetch)
      if (arrAirportFlightCategory === 'LIFR') {
        arrMarker.className = 'dep-arr-waypoint-img-magenta';
      } else if (arrAirportFlightCategory === 'IFR') {
        arrMarker.className = 'dep-arr-waypoint-img-red';
      } else if (arrAirportFlightCategory === 'MVFR') {
        arrMarker.className = 'dep-arr-waypoint-img-blue';
      } else if (arrAirportFlightCategory === 'VFR') {
        arrMarker.className = 'dep-arr-waypoint-img-green';
      } else {
        arrMarker.className = 'dep-arr-waypoint-img-brown';
      }

      const arrWaypointMarker = new mapboxgl.Marker(arrWaypointContainer)
        .setLngLat(
          mainRouteCoordinates[(mainRouteCoordinates || []).length - 1]
        )
        .addTo(map.current);
      arrMarker.onclick = () =>
        handleOpenAirportModal(
          arrAirportName,
          data.sDestIATA,
          arrAirportMETAR,
          arrAirportTAF,
          arrAirportICAO,
          notamList
        );

      markerList.push(arrWaypointMarker);
    });

  const arrIdText = document.createTextNode(arrWaypointId || 0);
  arrIdElement.appendChild(arrIdText);

  arrWaypointContainer.appendChild(arrMarker);
  arrWaypointContainer.appendChild(arrIdElement);

  // painting out main route
  const addMainRoute = () => {
    if (!map.current.getSource('mainRoute')) {
      map.current.addSource('mainRoute', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: mainRouteCoordinates,
          },
        },
      });
      if (mapSources) mapSources.add('mainRoute');
    }

    if (!map.current.getLayer('mainRoute')) {
      map.current.addLayer({
        id: 'mainRoute',
        type: 'line',
        source: 'mainRoute',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#003D50',
          'line-width': 3,
        },
      });
      if (mapLayers) mapLayers.add('mainRoute');
    }
  };

  if (map.current.isStyleLoaded()) {
    addMainRoute();
  } else {
    map.current.once('style.load', addMainRoute);
  }
}
